import { useEffect, useRef } from 'react';

/**
 * The chore icon grid. Ordered by where the job happens rather than
 * alphabetically, so a parent scanning for "the kitchen one" finds it fast.
 */
interface Props {
  value: string;
  onChange: (icon: string) => void;
  label?: string;
}

const GROUPS: { name: string; icons: string[] }[] = [
  {
    name: 'Kitchen',
    icons: ['🍽️', '🍳', '🥣', '🧑‍🍳', '🫖', '🥤', '🍱', '🧊', '🥄', '🍞'],
  },
  {
    name: 'Cleaning',
    icons: ['🧹', '🧽', '🧼', '🪣', '🧴', '🪠', '🧯', '✨', '🌀', '🫧'],
  },
  {
    name: 'Bathroom',
    icons: ['🚿', '🛁', '🚽', '🪥', '🧻', '🪞'],
  },
  {
    name: 'Bedroom & clothes',
    icons: ['🛏️', '🛌', '🧸', '👕', '👖', '🧦', '🧺', '🚪', '🪑', '🪟', '📦', '🗄️'],
  },
  {
    name: 'School',
    icons: ['🎒', '📚', '✏️', '📓', '🖍️', '⏰', '💻', '🎹', '⚽️'],
  },
  {
    name: 'Pets',
    icons: ['🐈', '🐕', '🐾', '🐟', '🐹', '🦎', '🥫'],
  },
  {
    name: 'Outside & other',
    icons: ['🗑️', '♻️', '🪴', '🌱', '🍂', '❄️', '🚲', '🚗', '📮', '🛒', '🛋️', '💡'],
  },
  {
    name: 'Anything',
    icons: ['✅', '⭐', '💪', '🎯', '🏆', '🔥', '❤️', '🙌'],
  },
];

export default function IconPicker({ value, onChange, label = 'Icon' }: Props): JSX.Element {
  const box = useRef<HTMLDivElement>(null);

  // Open on whatever is already chosen, so editing a chore shows its own icon
  // rather than the top of the list. Only on mount: scrolling away under the
  // user while they browse would be rude.
  useEffect(() => {
    box.current?.querySelector('.icon-chip.on')?.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div className="field">
      <span className="lab-text">{label}</span>
      <div className="icon-picker" ref={box}>
        {GROUPS.map((group) => (
          <div key={group.name} className="icon-group">
            <div className="icon-group-name">{group.name}</div>
            <div className="chips">
              {group.icons.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  className={`chip icon-chip${value === icon ? ' on' : ''}`}
                  onClick={() => onChange(icon)}
                  aria-label={icon}
                  aria-pressed={value === icon}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
